import { useMutation, useQuery } from '@tanstack/react-query'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { useMemo, useState } from 'react'
import { handleGetRequest, handlePutRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import React from 'react'
import { useAuth } from '../../contexts/AuthContext'
import ConfirmModal from '../common/ConfirmModal'
import useCountries from '../../hooks/useCountries'
import InputField from '../common/InputField'
import TextareaField from '../common/TextareaField'
import SelectField from '../common/SelectField'
import { useNavigate } from 'react-router-dom'

const ShopEditForm = () => {
  const navigate = useNavigate()

  const [formState, setFormState] = React.useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      shopName: '',
      description: '',
      buildingName: '',
      floor: '',
      shopNumber: '',
      countryId: '',
      cityId: '',
    }
  )
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [pendingUpdateData, setPendingUpdateData] = useState(null)

  const { currentUser } = useAuth()
  const shopId = currentUser?.id

  const { data: shopData } = useQuery({
    queryKey: QueryKeys.myShopDetails(currentUser.userType),
    queryFn: () => handleGetRequest(`${currentUser.userType}s/my-shop`),
    staleTime: 1000,
  })

  const { data: countriesData, isLoading: isLoadingCountries } = useCountries()

  const [originalValues, setOriginalValues] = useState({
    shopNumber: '',
    buildingName: '',
    floor: '',
  })

  const shop = shopData?.data
  const countries = countriesData?.data || []

  // Get cities for selected country
  const selectedCountry = countries.find((country) => country.id === parseInt(formState.countryId))
  const cities = selectedCountry?.cities || []

  useMemo(() => {
    if (shop) {
      setFormState({
        shopName: shop.shopName || '',
        description: shop.description || '',
        buildingName: shop.buildingName || '',
        floor: shop.floor || '',
        shopNumber: shop.shopNumber || '',
        countryId: shop.countryId || '',
        cityId: shop.cityId || '',
      })

      setOriginalValues({
        shopNumber: shop.shopNumber || '',
        buildingName: shop.buildingName || '',
        floor: shop.floor || '',
      })
    }
  }, [shop])

  const shopMutation = useMutation({
    mutationFn: async (shopData) => {
      const response = await handlePutRequest(`/${currentUser.userType}s/my-shop`, shopData)
      return response.data
    },
    onSuccess: () => {
      showToaster('success', 'Shop Updated Successfully')
      QueryKeys.myShopDetails.invalidate(currentUser.userType)
      QueryKeys.shopVerificationDetails.invalidate(currentUser.id)
      navigate('/my-shop')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    const { shopName, description, buildingName, floor, shopNumber, countryId, cityId } = formState

    const hasVerificationFieldsChanged =
      shopNumber !== originalValues.shopNumber ||
      buildingName !== originalValues.buildingName ||
      floor !== originalValues.floor

    const updatedShopData = {
      userId: shopId,
      shopName,
      description,
      shopNumber,
      buildingName,
      floor,
      countryId: parseInt(countryId) || null,
      cityId: parseInt(cityId) || null,
    }

    if (hasVerificationFieldsChanged) {
      setPendingUpdateData(updatedShopData)
      setIsConfirmModalOpen(true)
      return
    }

    shopMutation.mutate(updatedShopData)
  }

  const handleCountryChange = (countryId) => {
    setFormState({ countryId, cityId: '' }) // Reset city when country changes
  }

  return (
    <div className="">
      <form onSubmit={handleSubmit} className="space-y-4 flex flex-col">
        {/* Shop Name */}
        <InputField
          label="Shop Name"
          id="shopName"
          value={formState.shopName}
          onChange={(e) => setFormState({ shopName: e.target.value })}
          required
          placeholder="Enter shop name"
        />

        {/* Description */}
        <TextareaField
          label="Description"
          id="description"
          value={formState.description}
          onChange={(e) => setFormState({ description: e.target.value })}
          rows={3}
          placeholder="Describe your shop"
        />

        {/* Country */}
        <div>
          <label
            htmlFor="country"
            className="text-sm font-medium font-inter text-gray-600 mb-1 block"
          >
            Country <span className="text-red-500">*</span>
          </label>
          <SelectField
            id="country"
            required
            value={formState.countryId}
            onChange={(e) => handleCountryChange(e.target.value)}
            disabled={isLoadingCountries}
            className=""
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </SelectField>
        </div>

        {/* City */}
        <div>
          <label htmlFor="city" className="text-sm font-medium font-inter text-gray-600 mb-1 block">
            City <span className="text-red-500">*</span>
          </label>
          <SelectField
            id="city"
            required
            value={formState.cityId}
            onChange={(e) => setFormState({ cityId: e.target.value })}
            disabled={!formState.countryId}
            className=""
          >
            <option value="">Select a city</option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </SelectField>
        </div>

        {/* Building Name */}
        <InputField
          label="Building Name"
          id="buildingName"
          value={formState.buildingName}
          onChange={(e) => setFormState({ buildingName: e.target.value })}
          required
          placeholder="Enter building name"
        />

        {/* Floor */}
        <InputField
          label="Floor"
          id="floor"
          type="string"
          value={formState.floor}
          onChange={(e) => setFormState({ floor: e.target.value })}
          required
          min="0"
          placeholder="Enter floor number"
        />

        {/* Shop Number */}
        <InputField
          label="Shop Number"
          id="shopNumber"
          value={formState.shopNumber}
          onChange={(e) => setFormState({ shopNumber: e.target.value })}
          required
          placeholder="Enter shop number"
        />

        <div className="pt-4">
          <button
            type="submit"
            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-secondary-600 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-600 ${
              shopMutation.isLoading ? 'opacity-70 cursor-wait' : ''
            }`}
          >
            {shopMutation.isLoading ? 'Updating...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={async () => {
          await shopMutation.mutateAsync(pendingUpdateData)
          setPendingUpdateData(null)
        }}
        title="Reset Verification?"
        message="Changing your shop number, building name, or floor will remove your current verification. You’ll need to submit a new verification request and pay the verification fee again. Do you want to proceed?"
      />
    </div>
  )
}

export default ShopEditForm
